/**
 * Nota de crédito de un comprobante administrativo.
 *
 * Puerto de `Credito::note` + `views/menu/credito/note.php` (el formulario que el
 * listado de comprobantes abría con «Anular Mediante Nota de Crédito») y de su
 * emisión (`API/Credit::note_post`): se elige el **motivo** (`abc_codigo_tipo_nota`)
 * y la **serie** administrativa de nota (`document_series`, prefijo F o B según el
 * comprobante), y la API pide la nota al servicio de facturación, la registra
 * (`nota_credito`, `nota_credito_detalle` y `cpe_nota_credito`), marca el
 * comprobante afectado como «Nota de Credito» y libera los vales.
 */

import { AlertTriangle, BadgeX, FileDown, Info } from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import { useCatalogoNota, useEmitirNota } from '../comprobantes.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface Props {
  /** Venta a notar (`null` cierra el diálogo). */
  id: number | null;
  onClose: () => void;
  onAviso: (mensaje: string) => void;
}

export function ComprobanteNotaDialog({ id, onClose, onAviso }: Props) {
  const catalogo = useCatalogoNota(id);
  const emitir = useEmitirNota(id);

  const [codigo, setCodigo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [serie, setSerie] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [firma, setFirma] = useState('');

  // Se rellena al llegar el catálogo (guarda `listo`, sin efectos).
  if (catalogo.data && firma !== String(catalogo.data.venta_id)) {
    setFirma(String(catalogo.data.venta_id));
    setCodigo(catalogo.data.motivos[0]?.codigo ?? '');
    setMotivo(catalogo.data.motivos[0]?.descripcion ?? '');
    setSerie(catalogo.data.series[0] ?? '');
    setError(null);
  }

  const puedeEmitir = Boolean(codigo && motivo.trim() && serie) && !emitir.isPending;

  /** Emite la nota de crédito. */
  const enviar = () => {
    if (id === null) return;

    setError(null);
    emitir.mutate(
      { codigo, motivo: motivo.trim(), serie },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          onClose();
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo emitir la nota de crédito.'),
      },
    );
  };

  return (
    <Dialog
      open={id !== null}
      onClose={onClose}
      size="lg"
      title="Anular mediante nota de crédito"
      description={
        catalogo.data
          ? `${catalogo.data.documento ?? ''} · serie de nota con prefijo ${catalogo.data.prefijo}`
          : 'Emite la nota de crédito del comprobante afectado.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={!puedeEmitir}
            loading={emitir.isPending}
            leftIcon={<BadgeX className="size-4" aria-hidden="true" />}
            onClick={enviar}
          >
            Emitir nota
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning" title="Se anula el comprobante afectado">
          <span className={ICONO_EN_ALERTA}>
            <AlertTriangle className="size-4" aria-hidden="true" />
            La nota de crédito se pide al servicio de facturación con el <b>external_id</b> del
            comprobante; si la acepta, el comprobante queda en «Nota de Credito» y se liberan los
            vales que lo referenciaban.
          </span>
        </Alert>

        {catalogo.isLoading ? <Skeleton className="h-40 w-full" /> : null}

        {catalogo.isError ? (
          <Alert variant="error" title="No se pudieron leer los catálogos">
            {catalogo.error instanceof ApiError
              ? catalogo.error.message
              : 'Comprueba que la venta tenga comprobante.'}
          </Alert>
        ) : null}

        {catalogo.data ? (
          <>
            {catalogo.data.series.length === 0 ? (
              <Alert variant="error" title="Sin series de nota de crédito">
                No hay series administrativas de Nota de Crédito configuradas para el prefijo{' '}
                {catalogo.data.prefijo} (`document_series` con `documentTypeId = 4`).
              </Alert>
            ) : null}

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Motivo o sustento</span>
              <Select
                className={CONTROL}
                value={codigo}
                onChange={(evento) => {
                  const elegido = catalogo.data?.motivos.find((item) => item.codigo === evento.target.value);

                  setCodigo(evento.target.value);
                  setMotivo(elegido?.descripcion ?? '');
                }}
              >
                {catalogo.data.motivos.map((item) => (
                  <option key={item.codigo} value={item.codigo}>
                    {item.codigo} · {item.descripcion}
                  </option>
                ))}
              </Select>
            </label>

            <Input
              label="Descripción del motivo"
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
            />

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Serie de la nota de crédito</span>
              <Select
                className={CONTROL}
                value={serie}
                onChange={(evento) => setSerie(evento.target.value)}
              >
                {catalogo.data.series.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
              <span className="text-xs text-ink-500">
                Sólo se ofrecen las series administrativas activas del mismo tipo que el comprobante
                ({catalogo.data.prefijo}).
              </span>
            </label>
          </>
        ) : null}

        {error ? (
          <Alert variant="error" title="No se pudo emitir la nota">
            {error}
          </Alert>
        ) : null}

        <Alert variant="info" title="Qué se registra">
          <span className={ICONO_EN_ALERTA}>
            <Info className="size-4" aria-hidden="true" />
            La nota queda en `nota_credito` con sus líneas en `nota_credito_detalle`, su estado y sus
            enlaces en `cpe_nota_credito` (que es lo que lista Logística → Notas de Crédito), y el
            comprobante afectado pasa a «Nota de Credito».
          </span>
        </Alert>

        <p className="flex items-start gap-2 text-xs text-ink-500">
          <FileDown className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          El PDF, XML y CDR de la nota quedan guardados desde la respuesta del servicio de
          facturación.
        </p>
      </div>
    </Dialog>
  );
}
